import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PayoutTransaction,
  Settlement,
  SettlementApproveRequest,
  SettlementGenerateRequest,
  SettlementListItem,
  SettlementPayoutCreateRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { settlementQueryKey } from './useSettlementQuery';
import { payoutsQueryKey } from './usePayoutsQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, {
      status,
      ...(env.meta ? { meta: env.meta } : {}),
    });
  }
  throw new TransportError(`Settlement ${op} failed with status ${status}`, raw);
};

const invalidateRun = async (
  qc: ReturnType<typeof useQueryClient>,
  runId?: string,
) => {
  if (runId) {
    await qc.invalidateQueries({ queryKey: settlementQueryKey(runId) });
    await qc.invalidateQueries({ queryKey: payoutsQueryKey(runId) });
    await qc.invalidateQueries({ queryKey: ['admin', 'settlement-items', runId] });
  }
  await qc.invalidateQueries({ queryKey: ['admin', 'settlements'] });
};

export interface GenerateCycleInput {
  cycleId: string;
  body: SettlementGenerateRequest;
}

export const useGenerateSettlementCycleMutation = () => {
  const qc = useQueryClient();
  return useMutation<ReadonlyArray<SettlementListItem>, Error, GenerateCycleInput>({
    mutationFn: async ({ cycleId, body }) => {
      const path = '/admin/v1/settlements/cycles/{cycleId}/generate' as const;
      const concrete = path.replace('{cycleId}', cycleId);
      const idempotencyKey = idempotencyKeyFor('POST', concrete, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: {
          path: { cycleId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'generate');
      const payload = (data as { data: { items: SettlementListItem[] } }).data;
      return payload.items;
    },
    onSuccess: () => invalidateRun(qc),
  });
};

export interface ApproveSettlementInput {
  runId: string;
  body?: SettlementApproveRequest | undefined;
}

export const useApproveSettlementMutation = () => {
  const qc = useQueryClient();
  return useMutation<Settlement, Error, ApproveSettlementInput>({
    mutationFn: async ({ runId, body }) => {
      const path = '/admin/v1/settlements/{runId}/approve' as const;
      const concrete = path.replace('{runId}', runId);
      const idempotencyKey = idempotencyKeyFor('POST', concrete, body ?? {});
      const { data, error, response } = await apiClient.raw.POST(path, {
        body: body ?? {},
        params: {
          path: { runId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'approve');
      return (data as { data: Settlement }).data;
    },
    onSuccess: (_run, { runId }) => invalidateRun(qc, runId),
  });
};

export interface CreatePayoutInput {
  runId: string;
  body?: SettlementPayoutCreateRequest | undefined;
}

export const useCreatePayoutMutation = () => {
  const qc = useQueryClient();
  return useMutation<PayoutTransaction, Error, CreatePayoutInput>({
    mutationFn: async ({ runId, body }) => {
      const path = '/admin/v1/settlements/{runId}/payouts' as const;
      const concrete = path.replace('{runId}', runId);
      const idempotencyKey = idempotencyKeyFor('POST', concrete, body ?? {});
      const { data, error, response } = await apiClient.raw.POST(path, {
        body: body ?? {},
        params: {
          path: { runId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'payout');
      return (data as { data: PayoutTransaction }).data;
    },
    onSuccess: (_tx, { runId }) => invalidateRun(qc, runId),
  });
};
